import {match} from 'ts-pattern';

import {buildArray} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {
  ChartEncodingLegendIconType,
  ChartEncodingLegendItem,
} from '../types/ChartEncodingLegendItem';
import {ChartHighlightEncodingStyle} from '../types/ChartHighlightEncodingStyle';
import {ChartToolbarHighlights} from '../types/ChartToolbarHighlight';

interface GetChartEncodingLegendItemsParams {
  highlights: ChartToolbarHighlights;
  selectedSellerId: string | null;
  isSellerTypeEnabled: boolean;
  isSuspiciousFlagEnabled: boolean;
  encodingStyle: ChartHighlightEncodingStyle;
}

function getDefaultIcon(encodingStyle: ChartHighlightEncodingStyle): ChartEncodingLegendIconType {
  return match(encodingStyle)
    .with('scatter', () => 'circle' as const)
    .with('bar', () => 'bar' as const)
    .exhaustive();
}

function getPrivateSellerIcon(
  encodingStyle: ChartHighlightEncodingStyle
): ChartEncodingLegendIconType {
  return match(encodingStyle)
    .with('scatter', () => 'doughnut' as const)
    .with('bar', () => 'bar-hole' as const)
    .exhaustive();
}

function getRepricedIcons(encodingStyle: ChartHighlightEncodingStyle): {
  increased: ChartEncodingLegendIconType;
  decreased: ChartEncodingLegendIconType;
} {
  return match(encodingStyle)
    .with('scatter', () => ({
      increased: 'triangle-increased' as const,
      decreased: 'triangle-decreased' as const,
    }))
    .with('bar', () => ({
      increased: 'bar-caret-increased' as const,
      decreased: 'bar-notch-decreased' as const,
    }))
    .exhaustive();
}

function getBorderIcon(
  encodingStyle: ChartHighlightEncodingStyle,
  border: 'new' | 'suspicious' | 'dealer'
): ChartEncodingLegendIconType {
  return match({encodingStyle, border})
    .with({encodingStyle: 'scatter', border: 'new'}, () => 'border-new' as const)
    .with({encodingStyle: 'scatter', border: 'suspicious'}, () => 'border-suspicious' as const)
    .with({encodingStyle: 'scatter', border: 'dealer'}, () => 'border-dealer' as const)
    .with({encodingStyle: 'bar', border: 'new'}, () => 'bar-cap-new' as const)
    .with({encodingStyle: 'bar', border: 'suspicious'}, () => 'bar-cap-suspicious' as const)
    .with({encodingStyle: 'bar', border: 'dealer'}, () => 'bar-cap-dealer' as const)
    .exhaustive();
}

export function getChartEncodingLegendItems(
  params: GetChartEncodingLegendItemsParams
): ChartEncodingLegendItem[] {
  const repricedIcons = getRepricedIcons(params.encodingStyle);

  return buildArray<ChartEncodingLegendItem>([
    {
      id: 'default',
      label: i18n.t('entity.statisticsChart.legend.defaultDot'),
      icon: getDefaultIcon(params.encodingStyle),
    },
  ])
    .when(params.isSellerTypeEnabled && params.highlights.sellerType, {
      id: 'privateSeller',
      label: i18n.t('entity.statisticsChart.toolbar.privateSeller'),
      icon: getPrivateSellerIcon(params.encodingStyle),
    })
    .when(
      params.highlights.repriced,
      {
        id: 'repriced-increased',
        label: i18n.t('entity.statisticsChart.legend.priceIncreased'),
        icon: repricedIcons.increased,
      },
      {
        id: 'repriced-decreased',
        label: i18n.t('entity.statisticsChart.legend.priceDecreased'),
        icon: repricedIcons.decreased,
      }
    )
    .when(params.selectedSellerId !== null, {
      id: 'border-dealer',
      label: i18n.t('entity.statisticsChart.toolbar.dealer'),
      icon: getBorderIcon(params.encodingStyle, 'dealer'),
    })
    .when(params.isSuspiciousFlagEnabled && params.highlights.suspicious, {
      id: 'border-suspicious',
      label: i18n.t('page.advertisiments.suspicious'),
      icon: getBorderIcon(params.encodingStyle, 'suspicious'),
    })
    .when(params.highlights.new, {
      id: 'border-new',
      label: i18n.t('entity.statisticsChart.toolbar.new'),
      icon: getBorderIcon(params.encodingStyle, 'new'),
    });
}
